import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { filterUtils } from '@renderer/utils/filterUtils'
import { applyLUTThenOverlay, applyFilterOverlayImage, type FilterOverlayImage } from '@renderer/utils/lutUtils'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { useCountdown } from '@renderer/utils/countdownUtils'
import Preview from '@renderer/components/Preview'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { FaChevronLeft } from 'react-icons/fa'
import { FaChevronRight } from 'react-icons/fa'
import { imageUtils } from '@renderer/utils'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useTranslation } from 'react-i18next'
import { useSharedCountdown } from './hooks/useSharedCountdown'
import { backgroundFilterProcessor } from '@renderer/utils/backgroundFilterProcessor'
import NewspaperLayout from './components/NewspaperLayout'
import { getNormalizedCaptureRatio as getSharedNormalizedCaptureRatio } from '@renderer/utils/captureRatioUtils'
import { ProcessingModal } from '@renderer/components'

// Component to display filtered captured image
const FilteredImagePreview: React.FC<{
  imageUrl: string
  filterAdjustments?: Record<string, number>
  cachedFilteredUrl?: string
  filterMode?: 'manual' | 'lut'
  filterId?: string
  postLUTAdjustments?: { vignette?: number; grain?: number; blur?: number; clarity?: number; sharpeningAmount?: number }
  /** The filter's overlay layer; applies in both Manual and LUT mode. */
  filterOverlay?: FilterOverlayImage | null
}> = ({ imageUrl, filterAdjustments, cachedFilteredUrl, filterMode, filterId, postLUTAdjustments, filterOverlay }) => {
  const [filteredImageUrl, setFilteredImageUrl] = useState<string>(cachedFilteredUrl || imageUrl)
  const imgRef = useRef<HTMLImageElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (cachedFilteredUrl) {
      setFilteredImageUrl(cachedFilteredUrl)
      return
    }

    if (!imgRef.current || !canvasRef.current) return

    const img = imgRef.current
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')

    if (!ctx) return

    const applyFilter = (): void => {
      canvas.width = img.naturalWidth || img.width
      canvas.height = img.naturalHeight || img.height
      ctx.drawImage(img, 0, 0)

      if (filterMode === 'lut' && filterId) {
        applyLUTThenOverlay(ctx, {
          _id: filterId,
          filterMode: 'lut',
          postLUTAdjustments
        })
      } else if (filterAdjustments) {
        filterUtils.applyAdjustments(ctx, filterAdjustments)
      }
      applyFilterOverlayImage(ctx, filterOverlay)

      const filteredDataUrl = canvas.toDataURL()
      setFilteredImageUrl(filteredDataUrl)
    }

    if (img.complete) {
      applyFilter()
    } else {
      img.onload = applyFilter
      img.onerror = (): void => {
        console.error('Error loading image for filter preview')
        setFilteredImageUrl(imageUrl)
      }
    }

    return (): void => {
      if (img) {
        img.onload = null
        img.onerror = null
      }
    }
  }, [imageUrl, filterAdjustments, cachedFilteredUrl, filterMode, filterId, postLUTAdjustments, filterOverlay])

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <img
        ref={imgRef}
        src={imageUrl}
        alt="Captured photo"
        className="hidden"
        crossOrigin="anonymous"
      />
      <canvas ref={canvasRef} className="hidden" />
      <img
        src={filteredImageUrl}
        alt="Filtered photo"
        className="w-full h-full object-contain"
        crossOrigin="anonymous"
      />
    </div>
  )
}

const FilterPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { filters, config: globalConfig, getPageSettings } = useConfigStore()
  const config = globalConfig?.pages?.filter
  const { setSessionState, mode, userPreferences, logActivity, gallery } = useSessionStateStore()
  const sessionStateStore = useSessionStateStore
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null)
  const [previewCanvases, setPreviewCanvases] = useState<{ [key: string]: HTMLCanvasElement }>({})
  const [capturedImageUrl, setCapturedImageUrl] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const sliderRef = useRef<Slider>(null)
  const isLandscape =
    (globalConfig?.screenSize.height || 1080) < (globalConfig?.screenSize.width || 1920)
  const { captureRatio } = useSettingsStore((state) => state.cameraSettings)
  const [remainingTime, setRemainingTime] = useState<string>('--:--')
  const welcomeSettings = getPageSettings('welcome')
  
  // Use shared countdown for consistent UI
  const {
    remainingTime: sharedRemainingTime,
    isExpired: isSharedCountdownExpired
  } = useSharedCountdown()

  const getNormalizedCaptureRatio = (): number | undefined =>
    getSharedNormalizedCaptureRatio(captureRatio)

  const applyAfterCapture = config?.settings?.applyAfterCapture || false

  // Determine capture mode
  const captureMode = globalConfig?.pages?.capture?.settings?.mode || 'standard'

  // Determine if Filter page is before or after capture
  // If applyAfterCapture is false, Filter appears BEFORE capture (no global countdown yet)
  // If applyAfterCapture is true, Filter appears AFTER capture (use appropriate countdown)
  const isAfterCapture = applyAfterCapture

  // Initialize countdown with the useCountdown hook
  const filterTimeout = config?.settings?.filterTimeout || 0
  const countdown = useCountdown(filterTimeout)

  useEffect(() => {
    if (filters.length > 0) {
      setSelectedFilter(filters[0]._id)
    }
  }, [filters])

  // Navigate away if countdown expired (only after capture)
  useEffect(() => {
    if (isAfterCapture && isSharedCountdownExpired) {
      logActivity('Shared countdown expired on Filter page')
      // Navigate to Select to let it handle processing automatically
      navigate(`/${projectId}/select`)
    }
  }, [isAfterCapture, isSharedCountdownExpired])

  const handleNext = async (): Promise<void> => {
    if (!selectedFilter || isProcessing) return

    try {
      setIsProcessing(true)

      setSessionState({
        userPreferences: {
          filterId: selectedFilter
        }
      })
      const filterName = filters.find((f) => f._id === selectedFilter)?.name
      logActivity(`Selected filter: ${filterName || selectedFilter}`, { filterId: selectedFilter })

      // If applyAfterCapture is enabled, process all individual photos with the selected filter
      if (applyAfterCapture) {
        // Get all captured photos (excluding strips, gifs, etc.)
        const photosToProcess = gallery.capturedItems.filter(
          (item) =>
            item.photoUrl &&
            !item.id?.includes('dreambooth-strip') &&
            !item.id?.includes('dreambooth-gif') &&
            !item.id?.includes('dreambooth-animation')
        )

        // Process each photo with the selected filter
        for (const item of photosToProcess) {
          if (item.photoUrl && item.id) {
            const photoUrlToProcess = item.originalPhotoUrl || item.photoUrl
            await backgroundFilterProcessor.addToQueue(photoUrlToProcess, item.id, selectedFilter)
          }
        }

        // Wait for all filters to be processed
        // Poll until processing is complete (with timeout)
        const maxWaitTime = 30000 // 30 seconds max
        const startTime = Date.now()
        
        while (Date.now() - startTime < maxWaitTime) {
          const status = backgroundFilterProcessor.getProcessingStatus()
          if (!status.isProcessing && status.queueLength === 0) {
            break
          }
          await new Promise((resolve) => setTimeout(resolve, 100))
        }

        // Update gallery items to use filtered versions
        const { gallery: latestGallery } = sessionStateStore.getState()
        const updatedItems = latestGallery.capturedItems.map((item) => {
          if (item.photoUrl && item.filteredVersions?.[selectedFilter]) {
            // Replace photoUrl with filtered version
            return {
              ...item,
              photoUrl: item.filteredVersions[selectedFilter]
            }
          }
          return item
        })

        setSessionState({
          gallery: {
            ...latestGallery,
            capturedItems: updatedItems
          }
        })

        logActivity(`Applied filter ${filterName || selectedFilter} to ${photosToProcess.length} photos`, {
          filterId: selectedFilter,
          photosProcessed: photosToProcess.length
        })
      }

      setIsProcessing(false)

      // Navigate based on applyAfterCapture setting
      // When applyAfterCapture=true, always navigate to Select
      const nextPage = applyAfterCapture
        ? 'select'
        : mode === 'capture'
          ? 'frame'
          : welcomeSettings?.startWithPayment
            ? 'record'
            : 'checkout'
      navigate(`/${projectId}/${nextPage}`)
    } catch (error) {
      console.error('Error in handleNext:', error)
      setIsProcessing(false)
    }
  }

  // Set up countdown callbacks with dependencies on selectedFilter
  // Only use countdowns AFTER capture (when applyAfterCapture is true)
  // Before capture (applyAfterCapture = false): No countdown - just selecting filter for live preview
  // After capture:
  //   - Frame-based mode: Use shared countdown only
  //   - Standard mode: Use individual countdown
  useEffect(() => {
    if (isAfterCapture && captureMode === 'standard' && filterTimeout > 0) {
      // Set the onTick callback to update the remaining time display
      countdown.setOnTick(() => {
        setRemainingTime(countdown.getFormattedRemainingSeconds())
      })

      // Set the onComplete callback that will use the latest state
      countdown.setOnComplete(() => {
        const filterName = filters.find((f) => f._id === selectedFilter)?.name
        logActivity(
          `Filter selection timed out. Automatically selected filter: ${filterName || selectedFilter}`,
          {
            filterId: selectedFilter,
            timeoutDuration: filterTimeout
          }
        )
        handleNext()
      })
    }
  }, [selectedFilter, filterTimeout, setSessionState, captureMode, isAfterCapture])

  // Start the countdown on mount
  useEffect(() => {
    if (isAfterCapture && captureMode === 'standard' && filterTimeout > 0) {
      countdown.start()

      return (): void => {
        countdown.stop()
      }
    }
    return
  }, [filterTimeout, captureMode, isAfterCapture])

  useEffect(() => {
    const createFilterPreviews = async (): Promise<void> => {
      const previews: { [key: string]: HTMLCanvasElement } = {}

      // Check if we should use captured image (applyAfterCapture mode)
      const applyAfterCapture = globalConfig?.pages?.filter?.settings?.applyAfterCapture || false

      // If applyAfterCapture is enabled, use first captured image
      if (applyAfterCapture && gallery.capturedItems.length > 0) {
        const firstCapturedImageUrl = gallery.capturedItems[0].photoUrl
        if (!firstCapturedImageUrl) return

        // Store captured image URL for main preview
        setCapturedImageUrl(firstCapturedImageUrl)

        try {
          const response = await fetch(firstCapturedImageUrl)
          const capturedBlob = await response.blob()

          for (const filter of filters) {
            const canvas = document.createElement('canvas')
            canvas.width = 144
            canvas.height = 144
            const ctx = canvas.getContext('2d', {
              willReadFrequently: true
            }) as unknown as OffscreenCanvasRenderingContext2D
            if (ctx) {
              try {
                // Create image from captured blob
                const imageBitmap = await createImageBitmap(capturedBlob)
                const tempCanvas = document.createElement('canvas')
                tempCanvas.width = imageBitmap.width
                tempCanvas.height = imageBitmap.height
                const tempCtx = tempCanvas.getContext('2d', {
                  willReadFrequently: true
                })
                if (tempCtx) {
                  tempCtx.drawImage(imageBitmap, 0, 0)
                  if (filter.filterMode === 'lut') {
                    applyLUTThenOverlay(tempCtx, filter)
                  } else if (filter.adjustments) {
                    filterUtils.applyAdjustments(tempCtx, filter.adjustments)
                    applyFilterOverlayImage(tempCtx, filter.overlay)
                  }
                  imageBitmap.close()

                  // Scale and center the filtered image
                  const scale = Math.max(
                    canvas.width / tempCanvas.width,
                    canvas.height / tempCanvas.height
                  )
                  const x = (canvas.width - tempCanvas.width * scale) / 2
                  const y = (canvas.height - tempCanvas.height * scale) / 2
                  ctx.drawImage(
                    tempCanvas,
                    x,
                    y,
                    tempCanvas.width * scale,
                    tempCanvas.height * scale
                  )
                }
              } catch (error) {
                console.error('Error creating preview for filter:', filter._id, error)
              }
            }
            previews[filter._id] = canvas
          }
        } catch (error) {
          console.error('Error loading captured image for filter previews:', error)
          return
        }
      } else {
        // Original behavior: use cached thumbnails or live camera
        // Clear captured image URL when not in applyAfterCapture mode
        setCapturedImageUrl(null)

        const useLivePreview = globalConfig?.pages?.filter?.settings?.useLivePreview ?? true
        const filterPreviewImageUrl = globalConfig?.filterPreviewImageUrl

        // If useLivePreview is false, use cached thumbnails or generate them from filter preview image
        if (!useLivePreview) {
          // Check if we have cached thumbnails
          const hasCachedThumbnails = filters.some((f) => f.cachedThumbnail)

          if (hasCachedThumbnails) {
            // Use existing cached thumbnails
            for (const filter of filters) {
              if (filter.cachedThumbnail) {
                try {
                  const response = await fetch(filter.cachedThumbnail)
                  const blob = await response.blob()
                  const imageBitmap = await createImageBitmap(blob)

                  const canvas = document.createElement('canvas')
                  canvas.width = 144
                  canvas.height = 144
                  const ctx = canvas.getContext('2d', {
                    willReadFrequently: true
                  })

                  if (ctx) {
                    ctx.drawImage(imageBitmap, 0, 0, 144, 144)
                    imageBitmap.close()
                    previews[filter._id] = canvas
                  }
                } catch (error) {
                  console.error('Error loading cached thumbnail for filter:', filter._id, error)
                }
              }
            }
          } else if (
            filterPreviewImageUrl &&
            (filterPreviewImageUrl.startsWith('http') || filterPreviewImageUrl.startsWith('blob:'))
          ) {
            // Generate thumbnails on the fly from filter preview image
            try {
              const response = await fetch(filterPreviewImageUrl)
              const previewBlob = await response.blob()
              const previewImageBitmap = await createImageBitmap(previewBlob)

              for (const filter of filters) {
                const canvas = document.createElement('canvas')
                canvas.width = 144
                canvas.height = 144
                const ctx = canvas.getContext('2d', {
                  willReadFrequently: true
                })

                if (ctx) {
                  // Create a temporary canvas to process the filter
                  const tempCanvas = document.createElement('canvas')
                  tempCanvas.width = previewImageBitmap.width
                  tempCanvas.height = previewImageBitmap.height
                  const tempCtx = tempCanvas.getContext('2d', {
                    willReadFrequently: true
                  })

                  if (tempCtx) {
                    // Draw the preview image
                    tempCtx.drawImage(previewImageBitmap, 0, 0)

                    if (filter.filterMode === 'lut') {
                      applyLUTThenOverlay(tempCtx, filter)
                    } else if (filter.adjustments) {
                      filterUtils.applyAdjustments(tempCtx, filter.adjustments)
                      applyFilterOverlayImage(tempCtx, filter.overlay)
                    }

                    // Scale and center the filtered image to thumbnail size
                    const scale = Math.max(
                      canvas.width / tempCanvas.width,
                      canvas.height / tempCanvas.height
                    )
                    const x = (canvas.width - tempCanvas.width * scale) / 2
                    const y = (canvas.height - tempCanvas.height * scale) / 2

                    ctx.drawImage(
                      tempCanvas,
                      x,
                      y,
                      tempCanvas.width * scale,
                      tempCanvas.height * scale
                    )
                  }
                }
                previews[filter._id] = canvas
              }

              previewImageBitmap.close()
            } catch (error) {
              console.error('Error loading filter preview image for thumbnails:', error)
              // Fall through to live preview as last resort
            }
          }

          // If we still don't have previews (no cached thumbnails and no preview image URL),
          // fall back to live preview
          if (Object.keys(previews).length === 0) {
            const cameraBlob = await unifiedCameraUtils.capturePreview(
              userPreferences.selectedCamera as 'primary' | 'secondary'
            )
            const rawBlob = cameraBlob || null

            if (!rawBlob) return

            for (const filter of filters) {
              const canvas = document.createElement('canvas')
              canvas.width = 144
              canvas.height = 144
              const ctx = canvas.getContext('2d', {
                willReadFrequently: true
              }) as unknown as OffscreenCanvasRenderingContext2D
              if (ctx) {
                try {
                  const filterFn = (ctx: CanvasRenderingContext2D): void => {
                    if (filter.filterMode === 'lut') {
                      applyLUTThenOverlay(ctx, filter)
                    } else {
                      filterUtils.applyAdjustments(ctx, filter.adjustments || {})
                      applyFilterOverlayImage(ctx, filter.overlay)
                    }
                  }
                  const previewFrame = await imageUtils.processPreview(rawBlob, {
                    ratio: getNormalizedCaptureRatio(),
                    filter: filterFn
                  })
                  if (previewFrame instanceof Blob) {
                    const imageBitmap = await createImageBitmap(previewFrame)
                    const scale = Math.max(
                      canvas.width / imageBitmap.width,
                      canvas.height / imageBitmap.height
                    )
                    const x = (canvas.width - imageBitmap.width * scale) / 2
                    const y = (canvas.height - imageBitmap.height * scale) / 2
                    ctx.drawImage(
                      imageBitmap,
                      x,
                      y,
                      imageBitmap.width * scale,
                      imageBitmap.height * scale
                    )
                  }
                } catch (error) {
                  console.error('Error creating preview for filter:', filter.id, error)
                }
              }
              previews[filter._id] = canvas
            }
          }
        } else {
          // Use live camera preview (original behavior)
          const cameraBlob = await unifiedCameraUtils.capturePreview(
            userPreferences.selectedCamera as 'primary' | 'secondary'
          )
          const rawBlob = cameraBlob || null

          if (!rawBlob) return

          for (const filter of filters) {
            const canvas = document.createElement('canvas')
            canvas.width = 144
            canvas.height = 144
            const ctx = canvas.getContext('2d', {
              willReadFrequently: true
            }) as unknown as OffscreenCanvasRenderingContext2D
            if (ctx) {
              try {
                const filterFn = (ctx: CanvasRenderingContext2D): void => {
                  if (filter.filterMode === 'lut') {
                    applyLUTThenOverlay(ctx, filter)
                  } else {
                    filterUtils.applyAdjustments(ctx, filter.adjustments || {})
                    applyFilterOverlayImage(ctx, filter.overlay)
                  }
                }
                const previewFrame = await imageUtils.processPreview(rawBlob, {
                  ratio: getNormalizedCaptureRatio(),
                  filter: filterFn
                })
                if (previewFrame instanceof Blob) {
                  const imageBitmap = await createImageBitmap(previewFrame)
                  const scale = Math.max(
                    canvas.width / imageBitmap.width,
                    canvas.height / imageBitmap.height
                  )
                  const x = (canvas.width - imageBitmap.width * scale) / 2
                  const y = (canvas.height - imageBitmap.height * scale) / 2
                  ctx.drawImage(
                    imageBitmap,
                    x,
                    y,
                    imageBitmap.width * scale,
                    imageBitmap.height * scale
                  )
                }
              } catch (error) {
                console.error('Error creating preview for filter:', filter.id, error)
              }
            }
            previews[filter._id] = canvas
          }
        }
      }

      setPreviewCanvases(previews)
    }

    createFilterPreviews()
  }, [
    selectedFilter,
    globalConfig,
    filters,
    userPreferences.selectedCamera,
    getNormalizedCaptureRatio,
    gallery.capturedItems
  ])

  interface CustomArrowProps {
    onClick?: () => void
  }

  const NextArrow = ({ onClick }: CustomArrowProps): JSX.Element => {
    return (
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 cursor-pointer text-newspaper-black hover:opacity-70 transition-colors"
        onClick={onClick}
        role="button"
        aria-label={t('filter.nextFilter', 'Next filter')}
      >
        <FaChevronRight size={24} />
      </div>
    )
  }

  const PrevArrow = ({ onClick }: CustomArrowProps): JSX.Element => {
    return (
      <div
        aria-label={t('filter.prevFilter', 'Previous filter')}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 cursor-pointer text-newspaper-black hover:opacity-70 transition-colors"
        onClick={onClick}
        role="button"
      >
        <FaChevronLeft size={24} />
      </div>
    )
  }

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 5,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: '0px',
    focusOnSelect: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    beforeChange: (_: number, next: number): void => {
      setSelectedFilter(filters[next % filters.length]._id)
    },
    initialSlide: 0
  }

  // Duplicate filters array to prevent empty slides
  const extendedFilters = [...filters, ...filters]

  const { primaryColor } = globalConfig?.theme || {
    primaryColor: '#3B82F6',
    secondaryColor: '#FFFFFF'
  }

  return (
    <NewspaperLayout disableOverflow={true}>
      <div className="w-full h-[90%] relative" data-page="filter">
        {/* Processing Modal */}
        <ProcessingModal
          isVisible={isProcessing}
          title={t('filter.processing', 'Processing Filter...')}
          message={t('filter.processingMessage', 'Applying filter to your photos. Please wait...')}
          primaryColor={primaryColor}
        />

        {/* Shared Countdown Display - only show AFTER capture in frame-based mode */}
        {isAfterCapture && captureMode === 'frame-based' && (
          <div className="absolute top-8 right-8 z-50">
            <div className="border-2 border-double border-[#2a2a2a] bg-newspaper-white p-6 text-center">
              <div className="text-2xl text-newspaper-black mb-2 font-newspaper">
                {t('filter.timeRemaining', 'Time Remaining')}
              </div>
              <div className="font-bold text-newspaper-black font-newspaper text-5xl">
                {sharedRemainingTime}
              </div>
            </div>
          </div>
        )}

      {/* Preview Container */}
      <div className="absolute inset-0 m-auto flex items-center justify-center">
        <div className="relative w-full h-full flex items-center justify-center">
          {applyAfterCapture && capturedImageUrl ? (
            <FilteredImagePreview
              key={selectedFilter}
              imageUrl={capturedImageUrl}
              filterAdjustments={
                filters.find((f) => f._id === selectedFilter)?.adjustments as
                  | Record<string, number>
                  | undefined
              }
              cachedFilteredUrl={
                gallery.capturedItems.find((item) => item.photoUrl === capturedImageUrl)
                  ?.filteredVersions?.[selectedFilter || '']
              }
              filterMode={filters.find((f) => f._id === selectedFilter)?.filterMode}
              filterId={selectedFilter || undefined}
              postLUTAdjustments={filters.find((f) => f._id === selectedFilter)?.postLUTAdjustments}
filterOverlay={filters.find((f) => f._id === selectedFilter)?.overlay}
            />
          ) : (
            <Preview
              key={`${selectedFilter}-${captureRatio}`}
              adjustments={filters.find((f) => f._id === selectedFilter)?.adjustments}
              cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
              filter={selectedFilter || ''}
              postLUTAdjustments={filters.find((f) => f._id === selectedFilter)?.postLUTAdjustments}
filterOverlay={filters.find((f) => f._id === selectedFilter)?.overlay}
            />
          )}

          {/* Filter List - Positioned relative to Preview */}
          <div
            className={`absolute z-50 w-full ${isLandscape ? 'bottom-3' : 'bottom-12'}`}
            role="listbox"
          >
            <Slider ref={sliderRef} {...sliderSettings}>
              {extendedFilters.map((filter, index) => (
                <div
                  aria-label={filter.name}
                  key={`${filter._id}-${index}`}
                  className="px-6 py-6"
                  role="option"
                >
                  <div className="flex flex-col items-center h-[220px]">
                    <div
                      className={`w-36 h-36 overflow-hidden cursor-pointer border-2 border-double border-[#2a2a2a] rounded-none transition-all duration-200 ${
                        selectedFilter === filter._id
                          ? 'bg-newspaper-white border-4 border-[#1a1a1a] scale-110'
                          : 'bg-newspaper-white border-2 border-[#2a2a2a] opacity-70 scale-100'
                      }`}
                    >
                      {previewCanvases[filter._id] && (
                        <canvas
                          width="144"
                          height="144"
                          className="w-full h-full object-cover"
                          ref={(canvas) => {
                            if (canvas) {
                              const ctx = canvas.getContext('2d', { willReadFrequently: true })
                              if (ctx) {
                                ctx.imageSmoothingEnabled = true
                                ctx.imageSmoothingQuality = 'high'
                                ctx.drawImage(previewCanvases[filter._id], 0, 0, 144, 144)
                              }
                            }
                          }}
                        />
                      )}
                    </div>
                    <div
                      className={`h-12 flex items-center justify-center mt-6 transition-transform duration-200 ${
                        selectedFilter === filter._id ? 'scale-110' : 'scale-100'
                      }`}
                    >
                      <span
                        className={`text-2xl font-serif ${
                          selectedFilter === filter._id
                            ? 'text-newspaper-black font-bold'
                            : 'text-newspaper-black opacity-70'
                        }`}
                      >
                        {filter.name}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>

        {/* Next Button */}
        <div className="absolute -bottom-32 right-12 z-30">
          {/* Only show individual countdown AFTER capture in standard mode */}
          {isAfterCapture && captureMode === 'standard' && filterTimeout > 0 && (
            <span className="block text-center text-newspaper-black text-4xl mb-6 font-newspaper font-bold">
              {remainingTime}
            </span>
          )}
          <button
            type="button"
            onClick={handleNext}
            className="hover:opacity-90 cursor-pointer font-newspaper font-bold transition-all duration-500 select-none active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-[#1a1a1a] text-[#fafaf8] border-2 border-double border-[#2a2a2a] text-4xl px-16 py-5 min-w-[320px] tracking-wider rounded-none shadow-none"
          >
            {t('common.next', 'Next')}
          </button>
        </div>
      </div>
    </NewspaperLayout>
  )
}

export default FilterPage
